<template>
  <div class="page-container">
    <div class="page-title-container flex items-center">
      <wz-button variant="light" class="mr-3" @click="$router.push({ name: 'licenses-list' })">
        <i class="ri-arrow-left-line"></i> Volver
      </wz-button>
      <h1 class="page-title mb-0">Detalle de licencia</h1>
    </div>

    <div v-if="leave">
      <!-- Datos del colaborador -->
      <wz-card class="mb-4">
        <template #header><div class="wz-card-title">Datos del colaborador</div></template>
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2">
            <div class="detail-label text-muted text-sm">Nombre</div>
            <div class="detail-value font-bold">{{ leave.employeeFullName }}</div>
          </div>
          <div class="w-full md:w-1/3 px-2">
            <div class="detail-label text-muted text-sm">Empresa</div>
            <div class="detail-value">{{ leave.companyName || '-' }}</div>
          </div>
          <div class="w-full md:w-1/3 px-2">
            <div class="detail-label text-muted text-sm">Área</div>
            <div class="detail-value">{{ leave.areaName || '-' }}</div>
          </div>
        </div>
        <div class="flex flex-wrap mt-3 -mx-2">
          <div class="w-full md:w-1/3 px-2">
            <div class="detail-label text-muted text-sm">Responsable de RRHH</div>
            <div class="detail-value">{{ leave.hrResponsibleEmail || 'No asignado' }}</div>
          </div>
        </div>
      </wz-card>

      <!-- Datos de la licencia -->
      <wz-card class="mb-4">
        <template #header-title>Datos de la licencia</template>
        <template #header-actions>
          <wz-badge :variant="statusColor(leave.status)" pill>{{ statusLabel(leave.status) }}</wz-badge>
        </template>
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Tipo</div>
            <div class="detail-value font-bold">{{ leave.leaveTypeName }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2" v-if="leave.subtype">
            <div class="detail-label text-muted text-sm">Subtipo</div>
            <div class="detail-value">{{ subtypeLabel(leave.subtype) }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2" v-if="leave.beneficiaryName">
            <div class="detail-label text-muted text-sm">Familiar</div>
            <div class="detail-value">{{ leave.beneficiaryName }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2" v-if="leave.relationship">
            <div class="detail-label text-muted text-sm">Parentesco</div>
            <div class="detail-value">{{ relationshipLabel(leave.relationship) }}</div>
          </div>
        </div>
        <div class="flex flex-wrap mt-3 -mx-2">
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Fecha inicio</div>
            <div class="detail-value" v-local-date="leave.startDate"></div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Fecha fin</div>
            <div class="detail-value" v-local-date="leave.endDate"></div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Días</div>
            <div class="detail-value font-bold">{{ leave.calendarDays }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2" v-if="leave.eventLocation">
            <div class="detail-label text-muted text-sm">Ubicación</div>
            <div class="detail-value">{{ eventLocationLabel(leave.eventLocation) }}</div>
          </div>
        </div>
        <div class="flex flex-wrap mt-3 -mx-2">
          <div class="w-full md:w-1/4 px-2" v-if="leave.extensionDays">
            <div class="detail-label text-muted text-sm">Días de extensión</div>
            <div class="detail-value">{{ leave.extensionDays }}</div>
          </div>
          <div class="w-full md:w-1/4 px-2">
            <div class="detail-label text-muted text-sm">Remunerado</div>
            <div class="detail-value">{{ leave.isRemunerated ? 'Sí' : 'No' }}</div>
          </div>
        </div>
      </wz-card>

      <!-- Documento -->
      <wz-card class="mb-4">
        <template #header><div class="wz-card-title">Documento adjunto</div></template>
        <wz-alert v-if="leave.requiresDocumentation && !leave.hasDocument"
                  variant="warning" icon="ri-alert-line" class="mb-3">
          Documento pendiente. Debe subir el sustento antes de finalizar la licencia.
        </wz-alert>
        <div class="flex items-center" v-if="leave.hasDocument">
          <i class="ri-file-text-line mr-2 text-primary" style="font-size: 1.5rem;"></i>
          <span class="flex-grow">{{ leave.documentName }}</span>
          <wz-button variant="light" size="sm" class="mr-2" @click="downloadDocument" :disabled="downloading">
            <i class="ri-download-line mr-1"></i>{{ downloading ? 'Descargando...' : 'Descargar' }}
          </wz-button>
          <wz-button variant="light" size="sm" @click="$refs.docInput.click()">
            <i class="ri-refresh-line mr-1"></i>Reemplazar
          </wz-button>
        </div>
        <div class="flex items-center" v-else>
          <span class="text-muted mr-3">No se ha adjuntado documento.</span>
          <wz-button variant="primary" size="sm" @click="$refs.docInput.click()">
            <i class="ri-upload-2-line mr-1"></i>Adjuntar documento
          </wz-button>
        </div>
        <input ref="docInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style="display:none" @change="onDocSelected" />
      </wz-card>

      <!-- Observaciones -->
      <wz-card class="mb-4" v-if="leave.observations">
        <template #header><div class="wz-card-title">Observaciones</div></template>
        <p class="mb-0">{{ leave.observations }}</p>
      </wz-card>

      <!-- Acciones -->
      <div class="text-right mb-4">
        <wz-button v-if="canEdit" variant="primary" class="mr-2" @click="$router.push({ name: 'licenses-edit', params: { id: leave.id } })">
          <i class="ri-edit-line mr-1"></i>Editar
        </wz-button>
        <wz-button v-if="canCancel" variant="danger" @click="confirmCancelModal = true">
          <i class="ri-close-circle-line mr-1"></i>Cancelar licencia
        </wz-button>
        <wz-button v-if="leave && leave.status !== 'CANCELADA'" variant="danger" class="ml-2" @click="showAnnul = true">
          <i class="ri-close-circle-line mr-1"></i>Anular por error
        </wz-button>
      </div>
    </div>

    <wz-confirm-modal
      v-model="confirmCancelModal"
      title="Cancelar licencia"
      description="¿Está seguro que desea cancelar esta licencia?"
      accept-text="Cancelar licencia"
      accept-variant="danger"
      @confirm="onCancel"
    />

    <annul-by-mistake-modal
      v-model="showAnnul"
      :submitting="annulSubmitting"
      :entity-description="leave ? `${leave.employeeFullName || ''} — ${leave.leaveTypeName || 'licencia'} del ${leave.startDate} al ${leave.endDate}` : ''"
      @confirm="doAnnul"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { LeaveService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'
import AnnulByMistakeModal from '@/core/components/AnnulByMistakeModal.vue'

export default {
  name: 'LeaveDetail',
  components: { AnnulByMistakeModal },
  data() {
    return {
      leave: null,
      downloading: false,
      confirmCancelModal: false,
      showAnnul: false,
      annulSubmitting: false,
    }
  },
  computed: {
    canEdit() {
      return this.leave && (this.leave.status === 'REGISTRADA' || this.leave.status === 'EN_CURSO')
    },
    canCancel() {
      if (!this.leave || this.leave.status !== 'REGISTRADA') return false
      const today = new Date().toISOString().split('T')[0]
      return this.leave.startDate > today
    },
  },
  methods: {
    statusColor(s) { const f = Object.values(LocalConstants.LeaveStatus).find(x => x.key === s); return f ? f.color : 'dark' },
    statusLabel(s) { const f = Object.values(LocalConstants.LeaveStatus).find(x => x.key === s); return f ? f.label : s },
    subtypeLabel(s) { const f = LocalConstants.LeavePaternitySubtype[s]; return f ? f.label : s },
    relationshipLabel(r) { const f = LocalConstants.LeaveRelationship[r]; return f ? f.label : r },
    eventLocationLabel(l) { const f = LocalConstants.LeaveEventLocation[l]; return f ? f.label : l },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await LeaveService.get(this.$route.params.id)
        this.leave = resp.payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la licencia')
        this.$router.push({ name: 'licenses-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async onCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await LeaveService.cancel(this.leave.id)
        Alert.success('Licencia cancelada correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar la licencia')
      } finally {
        this.confirmCancelModal = false
        this.$store.dispatch('app/loading', false)
      }
    },
    async doAnnul(reason) {
      try {
        this.annulSubmitting = true
        await LeaveService.annulLeave(this.leave.id, reason)
        Alert.success('Licencia anulada por error')
        this.showAnnul = false
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular la licencia')
      } finally {
        this.annulSubmitting = false
      }
    },
    async onDocSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 10MB')
        this.$refs.docInput.value = ''
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        await LeaveService.uploadDocument(this.leave.id, file)
        Alert.success('Documento adjuntado correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir el documento')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.$refs.docInput.value = ''
      }
    },
    async downloadDocument() {
      try {
        this.downloading = true
        const resp = await LeaveService.downloadDocument(this.leave.id)
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.leave.documentName || 'documento-licencia')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el documento')
      } finally {
        this.downloading = false
      }
    },
  },
  created() {
    this.loadData()
  },
}
</script>

<style lang="stylus"></style>
